import { z } from 'zod';
export const SURGERY_STATUSES = ['planning', 'scheduled', 'done', 'cancelled'];
/** Itens do checklist pré-operatório. auto = o sistema confere sozinho; required = bloqueia o agendamento. */
export const CHECKLIST_ITEMS = {
    quote_accepted: { label: 'Orçamento aceito', auto: true, required: true },
    tcle_signed: { label: 'TCLE assinado pelo paciente', auto: true, required: true },
    exams: { label: 'Exames pré-operatórios conferidos pelo médico', auto: false, required: true },
    medication: { label: 'Suspensão de medicamentos orientada (anticoagulante, AAS, suplementos)', auto: false, required: false },
    instructions: { label: 'Orientações pré-operatórias enviadas ao paciente', auto: false, required: false },
    companion: { label: 'Acompanhante para a volta confirmado', auto: false, required: false },
    payment: { label: 'Pagamento (cobrança paga ou combinado)', auto: true, required: false },
    preop_photos: { label: 'Fotos pré-operatórias', auto: true, required: false },
    confirmed: { label: 'Paciente confirmou a cirurgia', auto: true, required: false },
};
export const MANUAL_ITEMS = ['exams', 'medication', 'instructions', 'payment', 'companion'];
export const checklistItemSchema = z.object({
    key: z.string(), label: z.string(), auto: z.boolean(), required: z.boolean(),
    state: z.enum(['done', 'pending', 'not_applicable']),
    detail: z.string().nullable(),
    doneByName: z.string().nullable(), doneAt: z.string().nullable(),
    /** atenção especial (ex.: anamnese com anticoagulante) */
    warning: z.string().nullable(),
});
export const setChecklistItemSchema = z.object({ state: z.enum(['done', 'not_applicable', 'pending']), note: z.string().trim().max(280).nullable().default(null) });
export const createSurgerySchema = z.object({ quoteId: z.uuid() });
export const scheduleSurgerySchema = z.object({
    professionalId: z.uuid(),
    startsAt: z.iso.datetime({ offset: true }),
    durationMin: z.number().int().min(60).max(16 * 60).default(480),
    notifyPatient: z.boolean().default(true),
});
export const TEAM_ROLES = ['surgeon', 'assistant', 'nurse', 'technician', 'anesthesiologist', 'other'];
export const surgeryRecordFieldsSchema = z.object({
    technique: z.enum(['FUE', 'FUT', 'DHI', 'other']).default('FUE'),
    anesthesiaType: z.enum(['local', 'sedation', 'general']).default('local'),
    anesthesiologist: z.string().trim().max(160).nullable().default(null),
    graftsExtracted: z.number().int().min(0).max(15000).nullable().default(null),
    graftsImplanted: z.number().int().min(0).max(15000).nullable().default(null),
    team: z.array(z.object({ name: z.string().trim().min(2).max(120), role: z.enum(TEAM_ROLES) })).max(20).default([]),
    areas: z.array(z.enum(['frontal', 'hairline', 'mid_scalp', 'crown', 'temples', 'beard', 'eyebrow', 'other'])).max(8).default([]),
    startedAt: z.iso.datetime({ offset: true }).nullable().default(null),
    endedAt: z.iso.datetime({ offset: true }).nullable().default(null),
    complications: z.string().trim().max(4000).default(''),
    notes: z.string().trim().max(8000).default(''),
}).refine((v) => v.anesthesiaType === 'local' || !!v.anesthesiologist, { message: 'Sedação ou geral exige anestesiologista', path: ['anesthesiologist'] })
    .refine((v) => v.graftsImplanted == null || v.graftsExtracted == null || v.graftsImplanted <= v.graftsExtracted, { message: 'Implantados não podem passar dos extraídos', path: ['graftsImplanted'] })
    .refine((v) => !v.startedAt || !v.endedAt || v.endedAt >= v.startedAt, { message: 'Término antes do início', path: ['endedAt'] });
export const surgeryRecordSchema = z.object({
    id: z.uuid(), status: z.enum(['draft', 'signed']), fields: surgeryRecordFieldsSchema,
    signedByName: z.string().nullable(), signedAt: z.string().nullable(), updatedAt: z.string(),
    addenda: z.array(z.object({ id: z.uuid(), text: z.string(), authorName: z.string(), createdAt: z.string() })),
});
export const surgerySchema = z.object({
    id: z.uuid(), patientId: z.uuid(), patientName: z.string(), quoteId: z.uuid(), quoteCode: z.string(),
    status: z.enum(SURGERY_STATUSES), unitName: z.string(), unitEnabled: z.boolean(),
    appointment: z.object({ id: z.uuid(), startsAt: z.string(), endsAt: z.string(), status: z.string(), professionalName: z.string().nullable() }).nullable(),
    checklist: z.array(checklistItemSchema),
    ready: z.boolean(),
    missing: z.array(z.string()),
    record: surgeryRecordSchema.nullable(),
    doneAt: z.string().nullable(), cancelReason: z.string().nullable(), createdAt: z.string(),
    plan: z.object({ graftsEstimateMin: z.number().int().nullable(), graftsEstimateMax: z.number().int().nullable(), technique: z.string().nullable() }),
});
export const surgerySummarySchema = surgerySchema.pick({ id: true, patientId: true, patientName: true, quoteCode: true, status: true, appointment: true, ready: true, doneAt: true }).extend({
    checklistDone: z.number().int(), checklistTotal: z.number().int(),
});
// ------------------------------------------------------------ unidade (habilitação para cirurgia)
export const FACILITY_TYPES = ['consultorio', 'tipo_i', 'tipo_ii', 'tipo_iii', 'tipo_iv'];
export const FACILITY_LABELS = {
    consultorio: 'Consultório', tipo_i: 'Estabelecimento tipo I', tipo_ii: 'Estabelecimento tipo II', tipo_iii: 'Estabelecimento tipo III', tipo_iv: 'Estabelecimento tipo IV',
};
export const unitSettingsSchema = z.object({
    id: z.uuid(), name: z.string(), facilityType: z.enum(FACILITY_TYPES).nullable(), technicalResponsibleUserId: z.uuid().nullable(),
    sanitaryLicenseExpiresAt: z.string().nullable(), surgeryEnabled: z.boolean(),
    doctors: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export const saveUnitSettingsSchema = z.object({
    facilityType: z.enum(FACILITY_TYPES).nullable(),
    technicalResponsibleUserId: z.uuid().nullable(),
    sanitaryLicenseExpiresAt: z.iso.date().nullable(),
});
//# sourceMappingURL=surgery.js.map